import type { UTM } from './posthogUTM';
import type {
  UserInfoAssets,
  UserInfoContextType,
} from '@/provider/UserInfoProvider';
import { type BottomBtnProps } from '@/app/ai-headshot-generator/select-images/components/BottomBtn';
import { type DownloadWay } from '@/app/(image-rmw)/watermark-removal/processing/components/Output_download';
import { type BusinessType } from '@/components/innerLink/config';

/**
 * @see posthog埋点
 * @nameRule 命名规范：功能板块_宾(before/on/after)_动_名
 */

/**
 * @description 埋点基础上报信息
 * @var user_id 用户的唯一id
 * @var email 用户的邮箱
 * @var user_identity 用户的身份, 1-非用户;2-匿名用户;3-登录用户
 * @var utm_source utm来源
 * @var utm_medium utm媒介
 * @var utm_campaign utm活动
 * @var utm_content utm influencer_name
 */
type BaseUserInfo = {
  user_id: UserInfoAssets['user_id'];
  email?: UserInfoAssets['email'];
  user_identity: UserInfoContextType['userIdentity'];
};
type BaseUtmInfo = Partial<UTM>;
type BaseEventMap = BaseUserInfo & BaseUtmInfo;

export type EventMap = {
  /**
   * @name user_visit_kaze
   * @description 用户点击进入我们的应用后上报utm(不会上报用户信息)
   */
  user_visit_kaze: BaseUtmInfo & {
    url: string;
  };

  /**
   * @name user_visit_kaze_duration_10
   * @description 用户点击进入我们的应用后上报utm(不会上报用户信息)
   */
  user_visit_kaze_duration_10: BaseUtmInfo & {
    url: string;
  };

  /**
   * @name view_after_visit_app
   * @description 用户点击进入我们的应用后上报utm(不会上报用户信息)
   */
  view_after_visit_app: BaseUtmInfo;

  /**
   * @name login_kaze
   * @description 用户登录我们的应用后（不包括匿名登录）
   * @var login_way 登录方式, "one-tap"(一键登录) | "google-login"(谷歌登录) |"email-login"(email登录)
   */
  login_kaze: BaseEventMap & {
    login_way: 'one-tap' | 'google-login' | 'email-login';
  };

  /**
   * @name user_click_banner
   * @description 用户点击banner后
   * @var banner_name banner名称
   */
  user_click_banner: BaseUtmInfo & {
    banner_name: 'promote_header_banner';
  };

  /**
   * @name price_success
   * @description 用户成功购买我们的产品后
   * @var product_name 产品名称
   */
  price_success: BaseEventMap & {
    product_name: string;
    pay_from_module:
      | 'watermark_removal'
      | 'id_photo'
      | 'restore_image'
      | 'remove_background'
      | 'pricing_page'
      | 'promote_header_banner'
      | 'ai_expand'
      | 'unknown';
  };

  /**
   * @name user_open_pricing_modal
   * @description 用户打开登录付费弹窗
   */
  user_open_pricing_modal: BaseEventMap & {
    open_way:
      | 'download_anonymous'
      | 'download_HD_anonymous'
      | 'download_HD_user'
      | 'rmw_anonymous'
      | 'rmw_user'
      | 'top_header_banner'
      | 'cancel_pricing'
      | 'BUSINESS_QUOTA_EXCEEDED'
      | 'rb_single_user'
      | 'rb_single_anonymous'
      | 'rb_single_download_anonymous'
      | 'rb_single_download_HD_anonymous'
      | 'rb_single_download_HD_user'
      | 'no_user';
  };

  /**
   * @name user_close_pricing_modal
   * @description 用户关闭登录付费弹窗
   */
  user_close_pricing_modal: BaseEventMap & {
    open_way:
      | 'download_anonymous'
      | 'download_HD_anonymous'
      | 'download_HD_user'
      | 'rmw_anonymous'
      | 'rmw_user'
      | 'top_header_banner'
      | 'cancel_pricing'
      | 'BUSINESS_QUOTA_EXCEEDED'
      | 'rb_single_user'
      | 'rb_single_anonymous'
      | 'rb_single_download_anonymous'
      | 'rb_single_download_HD_anonymous'
      | 'rb_single_download_HD_user'
      | 'no_user';
  };

  /**
   * @name batch_rmw_when_upload
   * @description 批量去水印 - 用户批量选择图像时
   */
  batch_rmw_after_select: BaseEventMap & {
    before_counts: number; // 用户已经上传图像数量
    single_counts: number; // 用户单次选择上传的图像数量
    over_limit_counts: number; // 用户本次选择图像超过的最大限制张数（历史上传+本次上传-最大限制20）
  };

  /**
   * @name batch_rmw_after_click_add_more
   * @description 批量去水印 - 用户点击添加“+”更多图像时
   */
  batch_rmw_after_click_add_more: BaseEventMap & {
    whichTimes: number; // number代表用户第几次点击（刷新/重进页面重制为0）
  };

  /**
   * @name batch_rmw_open_smear
   * @description 批量去水印 - 用户打开涂鸦模式
   */
  batch_rmw_open_smear: BaseEventMap;

  /**
   * @name batch_rmw_after_smear
   * @description 批量去水印 - 用户关闭涂鸦模式
   */
  batch_rmw_after_smear: BaseEventMap & {
    close_way: 'cancel' | 'save';
  };

  /**
   * @name batch_rmw_click_remove_watermark_btn
   * @description 批量去水印 - 用户点击「Remove Watermark」按钮
   */
  batch_rmw_click_remove_watermark_btn: BaseEventMap & {
    all_counts: number; // 上传的所有图像数量（包括解析生成失败、上传失败的图像）
    could_rmw_counts: number; // 能够去水印的图像数量
    could_rmw_images: string[]; // 去水印图像的链接list（涂鸦图 & 原图， 有涂鸦就不记录原图）
    rmw_mode: 'pro' | 'lite'; // 去水印模式
  };

  /**
   * @name batch_rmw_show_no_enough_credits
   * @description 批量去水印 - 用户点击「Remove Watermark」按钮时，剩余积分不足
   */
  batch_rmw_show_no_enough_credits: BaseEventMap & {
    remain_credits: number; // 剩余多少积分
    need_credits: number; // 需要多少积分
  };

  /**
   * @name batch_rmw_click_buy_credits_btn
   * @description 批量去水印 - 用户点击「Buy More Credits」
   */
  batch_rmw_click_buy_credits_btn: BaseEventMap;

  /**
   * @name batch_rmw_click_adjust_upload_btn
   * @description 批量去水印 - 用户点击「Adjust Upload」
   */
  batch_rmw_click_adjust_upload_btn: BaseEventMap;

  /**
   * @name batch_rmw_after_create_tasks
   * @description 批量去水印 - 创建去水印任务
   */
  batch_rmw_after_create_tasks: BaseEventMap & {
    ever_paid: boolean; // 是否为付费用户
    all_counts: number; // 上传的所有图像（包括解析生成、上传失败的图像）
    could_rmw_counts: number; // 能够去水印的图像数量
    could_rmw_images: string[]; // 去水印图像的链接list（涂鸦图>原图）
    rmw_mode: 'pro' | 'lite'; // 去水印模式
    status: 'success' | 'fail'; // 状态
    reason: string; // 服务端返回的一些原因
  };

  /**
   * @name batch_rmw_click_download
   * @description 批量去水印 - 用户点击「Download HD」
   */
  batch_rmw_click_download: BaseEventMap & {
    download_way: DownloadWay;
  };

  /**
   * @name batch_rmw_after_download_fail
   * @description 批量去水印 - 下载失败
   */
  batch_rmw_after_download_fail: BaseEventMap & {
    download_way: DownloadWay;
    reason: string; // 前端错误信息
  };

  /**
   * @name batch_rmw_click_my_projects
   * @description 批量去水印 - 用户点击「My Projects」
   */
  batch_rmw_click_my_projects: BaseEventMap;

  /**
   * @name batch_rmw_rate
   * @description 评分数据埋点
   */
  batch_rmw_rate: BaseEventMap & {
    rate: number; // 用户评分
    output_image_url: string; // 用户评分的url链接
  };

  /**
   * @name batch_upload_img_fail
   * @description 批量业务 - 图片上传失败
   */
  batch_upload_img_fail: BaseEventMap & {
    title: string;
    type: string;
    errorInfo: string;
    otherInfo?: string;
  };

  /**
   * @name batch_restore_after_select
   * @description 批量图像修复 - 用户批量选择图像时
   */
  batch_restore_after_select: BaseEventMap & {
    before_counts: number; // 用户已经上传图像数量
    single_counts: number; // 用户单次选择上传的图像数量
    over_limit_counts: number; // 用户本次选择图像超过的最大限制张数（历史上传+本次上传-最大限制20）
  };

  /**
   * @name batch_restore_after_click_add_more
   * @description 批量图像修复 - 用户点击添加"+"更多图像时
   */
  batch_restore_after_click_add_more: BaseEventMap & {
    whichTimes: number; // number代表用户第几次点击（刷新/重进页面重制为0）
  };

  /**
   * @name batch_restore_click_restore_image_btn
   * @description 批量图像修复 - 用户点击「Repair Image」按钮
   */
  batch_restore_click_restore_image_btn: BaseEventMap & {
    all_counts: number; // 上传的所有图像数量（包括解析生成失败、上传失败的图像）
    could_repair_counts: number; // 能够修复的图像数量
    could_repair_images: string[]; // 修复图像的链接list
    repair_mode: 'pro' | 'lite'; // 修复模式
  };

  /**
   * @name batch_restore_show_no_enough_credits
   * @description 批量图像修复 - 用户点击「Repair Image」按钮时，剩余积分不足
   */
  batch_restore_show_no_enough_credits: BaseEventMap & {
    remain_credits: number; // 剩余多少积分
    need_credits: number; // 需要多少积分
  };

  /**
   * @name batch_restore_click_buy_credits_btn
   * @description 批量图像修复 - 用户点击「Buy More Credits」
   */
  batch_restore_click_buy_credits_btn: BaseEventMap;

  /**
   * @name batch_restore_click_adjust_upload_btn
   * @description 批量图像修复 - 用户点击「Adjust Upload」
   */
  batch_restore_click_adjust_upload_btn: BaseEventMap;

  /**
   * @name batch_restore_after_create_tasks
   * @description 批量图像修复 - 创建图像修复任务
   */
  batch_restore_after_create_tasks: BaseEventMap & {
    ever_paid: boolean; // 是否为付费用户
    all_counts: number; // 上传的所有图像（包括解析生成、上传失败的图像）
    could_repair_counts: number; // 能够修复的图像数量
    could_repair_images: string[]; // 修复图像的链接list
    repair_mode: 'pro' | 'lite'; // 修复模式
    status: 'success' | 'fail'; // 状态
    reason: string; // 服务端返回的一些原因
  };

  /**
   * @name batch_restore_click_download
   * @description 批量图像修复 - 用户点击「Download HD」
   */
  batch_restore_click_download: BaseEventMap & {
    download_way: DownloadWay;
  };

  /**
   * @name batch_restore_after_download_fail
   * @description 批量图像修复 - 下载失败
   */
  batch_restore_after_download_fail: BaseEventMap & {
    download_way: DownloadWay;
    reason: string; // 前端错误信息
  };

  /**
   * @name batch_restore_click_my_projects
   * @description 批量图像修复 - 用户点击「My Projects」
   */
  batch_restore_click_my_projects: BaseEventMap;

  /**
   * @name batch_restore_rate
   * @description 批量图像修复 - 评分数据埋点
   */
  batch_restore_rate: BaseEventMap & {
    rate: number; // 用户评分
    output_image_url: string; // 用户评分的url链接
  };

  /**
   * @name rb_single_click_upload_image
   * @description 单张去背景 - 用户在landing page点击「Upload」按钮上传图像
   */
  rb_single_click_upload_image: BaseEventMap & {
    path: 'landingpage' | 'processing';
  };

  /**
   * @name rb_single_after_upload_image
   * @description 单张去背景 - 用户上传图像后
   */
  rb_single_after_upload_image: BaseEventMap & {
    status: 'success' | 'fail'; // 上传状态
    access_url: string; // 上传成功后的url链接
    reason: string; // 上传失败的原因
  };

  /**
   * @name rb_single_click_remove_background
   * @description 单张去背景 - 用户在 processing 页面点击「Remove Background」按钮
   */
  rb_single_click_remove_background: BaseEventMap;

  /**
   * @name rb_single_after_remove_background
   * @description 单张去背景 - 用户在 processing 页面点击「Remove Background」按钮后
   */
  rb_single_after_remove_background: BaseEventMap & {
    status: 'success' | 'fail'; // 状态
    reason: string; // 去背景失败的原因
  };

  /**
   * @name rb_single_download
   * @description 单张去背景 - 用户在 processing 页面点击「Download HD」按钮下载图像
   */
  rb_single_download: BaseEventMap & {
    download_way: DownloadWay;
  };

  rb_single_download_fail: BaseEventMap & {
    download_way: DownloadWay;
    reason: string; // 下载失败的原因
  };

  /**
   * @name rb_single_click_select_background_color
   * @description 单张去背景 - 用户在 processing 页面点击「Select Background Color」按钮
   */
  rb_single_click_select_background_color: BaseEventMap & {
    background_color: 'transparent' | 'black' | 'white' | 'custom';
    custom_color: string;
  };

  /**
   * @name rb_single_rate
   * @description 单张去背景 - 用户在 processing 页面点击「Rate」按钮
   */
  rb_single_rate: BaseEventMap & {
    rate: number; // 用户评分
    output_image_url: string; // 用户评分的url链接
  };

  /**
   * @name price_success_ai_headshots
   * @description 用户成功购买AI证件照后
   * @var product_name 产品名称
   */
  price_success_ai_headshots: BaseEventMap & {
    product_name: string;
    pay_from_module: 'id_photo';
  };

  /**
   * @name invite_link_generated
   * @description 用户成功生成邀请链接后
   */
  invite_link_generated: BaseEventMap & {
    invite_code: string;
    generate_link: string;
  };

  /**
   * @name login_after_click_free_btn
   * @description 未登录用户点击"登录/付费"的 Get Started 按钮
   */
  login_after_click_free_btn: BaseUtmInfo & {
    user_identity: UserInfoContextType['userIdentity'];
  };

  /**
   * @name user_not_exist_after_click_price_btn
   * @description 未登录用户点击"登录/付费"的 Get Pro的 "Subscribe now" 按钮或 Pay as you Go的 "Buy now" 按钮
   */
  user_not_exist_after_click_price_btn: BaseUtmInfo & {
    user_identity: UserInfoContextType['userIdentity'];
    btn_name: 'Subscribe now' | 'Buy now';
    btn_product_name: 'Just One Image' | 'Pay as You Go' | 'Get Pro';
  };

  /**
   * @name user_after_click_price_btn
   * @description 登录用户点击"登录/付费"的 Get Pro的 "Subscribe now" 按钮或 Pay as you Go的 "Buy now" 按钮
   */
  user_after_click_price_btn: BaseEventMap & {
    btn_name: 'Subscribe now' | 'Buy now';
    btn_product_name: 'Just One Image' | 'Pay as You Go' | 'Get Pro';
  };

  /**
   * @name ai_headshots_landing_cta_click
   * @description 用户点击AI证件照的landing页的CTA按钮
   */
  ai_headshots_click_landing_cta: BaseUtmInfo;

  /**
   * @name ai_headshots_continue_guide_cta
   * @description 用户点击上传照片指南页面continue cta
   */
  ai_headshots_continue_guide_cta: BaseUtmInfo;

  /**
   * @name ai_headshots_continue_scene_select
   * @description 用户选择scene(style)选项点击continue
   */
  ai_headshots_continue_scene_select: BaseEventMap & {
    scene: BottomBtnProps['scene'];
  };

  /**
   * @name ai_headshots_continue_flexibility_select
   * @description 用户选择flexibility(美颜)选项点击continue
   */
  ai_headshots_continue_flexibility_select: BaseEventMap & {
    flexibility: BottomBtnProps['flexibility'];
  };

  /**
   * @name ai_headshots_continue_gender_select
   * @description 用户选择性别选项点击continue
   */
  ai_headshots_continue_gender_select: BaseEventMap & {
    gender: BottomBtnProps['gender'];
  };

  /**
   * @name ai_headshots_continue_age_select
   * @description 用户选择年龄段点击continue
   */
  ai_headshots_continue_age_select: BaseEventMap & {
    age: BottomBtnProps['age'];
  };

  /**
   * @name ai_headshots_continue_ethnicity_select
   * @description 用户选择人种选项点击continue
   */
  ai_headshots_continue_ethnicity_select: BaseEventMap & {
    ethnicity: BottomBtnProps['ethnicity'];
  };

  /**
   * @name ai_headshots_continue_bodyType_select
   * @description 用户选择身材选项点击continue
   */
  ai_headshots_continue_bodyType_select: BaseEventMap & {
    bodyType: BottomBtnProps['bodyType'];
  };

  /**
   * @name ai_headshots_upload_image
   * @description 用户上传AI证件照原图
   * @var product_name 产品名称
   */
  ai_headshots_upload_image: BaseEventMap & {
    file_name: string;
    file_type: string;
    upload_res_status: 'success' | 'failed' | 'error';
    upload_res: string;
  };

  /**
   * @name user_use_aih_before
   * @description 用户使用AI证件照之前(调接口之前)
   */
  user_use_aih_before: BaseEventMap & {
    photosUrls: string;
  };

  /**
   * @name user_use_aih
   * @description 成功使用AI证件照(成功调接口之后)
   */
  user_use_aih: BaseEventMap;

  /**
   * @name ai_headshots_generate_failed
   * @description 任务创建失败页面展示
   */
  ai_headshots_generate_failed: BaseEventMap;

  /**
   * @name ai_headshots_generate_success
   * @description 任务创建完成页面展示（Done）
   */
  ai_headshots_generate_success: BaseEventMap;

  /**
   * @name ai_headshots_visit_my_project
   * @description 用户Done页面访问my project cta
   */
  ai_headshots_visit_my_project: BaseEventMap;

  /**
   * @name ai_headshots_click_upload_image
   * @description 证件照用户点击upload cta(拖拽不算)
   */
  ai_headshots_click_upload_image: BaseEventMap;

  /**
   * @name ai_headshots_click_pricing_cta
   * @description 证件照用户点击pricing table支付cta
   */
  ai_headshots_click_pricing_cta: BaseEventMap;

  /**
   * @name ai_headshots_input_phone_number
   * @description 证件照用户Done页面输入手机号
   */
  ai_headshots_input_phone_number: BaseEventMap & {
    phone_number: string;
  };

  /**
   * @name ai_headshots_open_phone_notify
   * @description 证件照用户Done页面输入手机号后开启Notify me
   */
  ai_headshots_after_phone_notify: BaseEventMap;

  /**
   * @name ai_headshots_rate
   * @description 用户评分
   */
  ai_headshots_rate: BaseEventMap & {
    rate: number; // 用户评分
    task_id: string; // 任务id
    content: string; // 用户反馈内容
  };

  /**
   * @name inner_link_click
   * @description 用户点击内部链接
   */
  inner_link_click: BaseEventMap & {
    path_name: string; // 当前页面path name
    link_name: BusinessType; // 点击的链接名称
  };
  /**
   * @name performance_upload_img_success
   * @description 性能上报 - 批量去水印 - 批量去水印接口从发送请求到收到响应
   */
  performance_rmw_finish_rmw_success: {
    api: string; // api
    duration: number; // 耗时
  };

  /**
   * @name performance_rmw_result_img_load_success
   * @description 性能上报 - 批量去水印 - 结果图像加载成功
   */
  performance_rmw_result_img_load_success: {
    url: string; // 结果图像的url
    size: string; // 图像大小
    duration: number; // 结果图像加载耗时（从获取到渲染完成）
    duration_1K: number; // 渲染1KB的平均耗时
  };

  /**
   * @name performance_rmw_overall_poll_success
   * @description 性能上报 - 批量去水印 - 整体轮询从开始到结束
   */
  performance_rmw_overall_poll_success: {
    pollCount: number; // 轮询次数
    all_size: string; // 所有图像大小 - 单位MB
    duration: number; // 耗时
  };

  /**
   * @name performance_rmw_batch_download_img_success
   * @description 性能上报 - 批量去水印 - 图像下载从点击到下载完成
   */
  performance_rmw_batch_download_img_success: {
    size: string; // 图像大小
    duration: number; // 耗时
    duration_1K: number; // 下载1KB的平均耗时
  };

  /**
   * @name performance_rmw_batch_download_zip_success
   * @description 性能上报 - 批量去水印 - 图像批量下载zip从点击到下载完成
   */
  performance_rmw_batch_download_zip_success: {
    all_size: string; // 图像总大小
    size: string; // 资源大小
    duration: number; // 耗时
    duration_1K: number; // 下载1KB的平均耗时
  };
};

export const posthog_points: {
  [K in keyof EventMap]: string; // posthog_points 的键必须是 EventMap 的键，值是 string
} = {
  user_visit_kaze: 'user_visit_kaze',
  user_visit_kaze_duration_10: 'user_visit_kaze_duration_10',
  view_after_visit_app: 'view_after_visit_app',
  login_kaze: 'login_after_login_app',
  user_click_banner: 'user_click_banner',
  user_open_pricing_modal: 'user_open_pricing_modal',
  user_close_pricing_modal: 'user_close_pricing_modal',
  price_success: 'pricing_after_price_product',
  price_success_ai_headshots: 'ai_headshots_after_price_product',

  batch_rmw_after_select: 'batch_rmw_after_select',
  batch_rmw_after_click_add_more: 'batch_rmw_after_click_add_more',
  batch_rmw_open_smear: 'batch_rmw_open_smear',
  batch_rmw_after_smear: 'batch_rmw_after_smear',
  batch_rmw_click_remove_watermark_btn: 'batch_rmw_click_remove_watermark_btn',
  batch_rmw_show_no_enough_credits: 'batch_rmw_show_no_enough_credits',
  batch_rmw_click_buy_credits_btn: 'batch_rmw_click_buy_credits_btn',
  batch_rmw_click_adjust_upload_btn: 'batch_rmw_click_adjust_upload_btn',
  batch_rmw_after_create_tasks: 'batch_rmw_after_create_tasks',
  batch_rmw_click_download: 'batch_rmw_click_download',
  batch_rmw_after_download_fail: 'batch_rmw_after_download_fail',
  batch_rmw_click_my_projects: 'batch_rmw_click_my_projects',
  batch_rmw_rate: 'batch_rmw_rate',

  batch_upload_img_fail: 'batch_upload_img_fail',

  batch_restore_after_select: 'batch_restore_after_select',
  batch_restore_after_click_add_more: 'batch_restore_after_click_add_more',
  batch_restore_click_restore_image_btn:
    'batch_restore_click_restore_image_btn',
  batch_restore_show_no_enough_credits: 'batch_restore_show_no_enough_credits',
  batch_restore_click_buy_credits_btn: 'batch_restore_click_buy_credits_btn',
  batch_restore_click_adjust_upload_btn:
    'batch_restore_click_adjust_upload_btn',
  batch_restore_after_create_tasks: 'batch_restore_after_create_tasks',
  batch_restore_click_download: 'batch_restore_click_download',
  batch_restore_after_download_fail: 'batch_restore_after_download_fail',
  batch_restore_click_my_projects: 'batch_restore_click_my_projects',
  batch_restore_rate: 'batch_restore_rate',

  rb_single_click_upload_image: 'rb_single_click_upload_image',
  rb_single_after_upload_image: 'rb_single_after_upload_image',
  rb_single_click_remove_background: 'rb_single_click_remove_background',
  rb_single_after_remove_background: 'rb_single_after_remove_background',
  rb_single_download: 'rb_single_download',
  rb_single_download_fail: 'rb_single_download_fail',
  rb_single_click_select_background_color:
    'rb_single_click_select_background_color',
  rb_single_rate: 'rb_single_rate',

  user_use_aih_before: 'ai_headshots_before_use_ai_headshots',
  user_use_aih: 'ai_headshots_after_use_ai_headshots',
  ai_headshots_upload_image: 'ai_headshots_after_upload_image',
  ai_headshots_click_landing_cta: 'ai_headshots_click_landing_cta',
  ai_headshots_continue_guide_cta: 'ai_headshots_continue_guide_cta',
  ai_headshots_continue_scene_select: 'ai_headshots_continue_scene_select',
  ai_headshots_continue_flexibility_select:
    'ai_headshots_continue_flexibility_select',
  ai_headshots_continue_gender_select: 'ai_headshots_continue_gender_select',
  ai_headshots_continue_age_select: 'ai_headshots_continue_age_select',
  ai_headshots_continue_ethnicity_select:
    'ai_headshots_continue_ethnicity_select',
  ai_headshots_continue_bodyType_select:
    'ai_headshots_continue_bodyType_select',
  ai_headshots_generate_failed: 'ai_headshots_generate_failed',
  ai_headshots_generate_success: 'ai_headshots_generate_success',
  ai_headshots_visit_my_project: 'ai_headshots_visit_my_project',
  ai_headshots_click_upload_image: 'ai_headshots_click_upload_image',
  ai_headshots_click_pricing_cta: 'ai_headshots_click_pricing_cta',
  ai_headshots_input_phone_number: 'ai_headshots_input_phone_number',
  ai_headshots_after_phone_notify: 'ai_headshots_after_phone_notify',
  ai_headshots_rate: 'ai_headshots_rate',

  invite_link_generated: 'invite_link_generated',
  login_after_click_free_btn: 'login_after_click_free_btn',
  user_not_exist_after_click_price_btn: 'user_not_exist_after_click_price_btn',
  user_after_click_price_btn: 'user_after_click_price_btn',

  inner_link_click: 'inner_link_click',

  performance_rmw_finish_rmw_success: 'performance_rmw_finish_rmw_success',
  performance_rmw_result_img_load_success:
    'performance_rmw_result_img_load_success',
  performance_rmw_overall_poll_success: 'performance_rmw_overall_poll_success',
  performance_rmw_batch_download_img_success:
    'performance_rmw_batch_download_img_success',
  performance_rmw_batch_download_zip_success:
    'performance_rmw_batch_download_zip_success',
};
